/* Magna e Spigni — collezione.js (fase 3)
   <mes-collezione>: filtri e ordinamento via Section Rendering API con URL condivisibile,
   "Mostra altri" che accoda, blocco "Ultimi pezzi" dall'Archivio.
   <mes-aggiunta-rapida>: pulsanti taglia sulla card -> /cart/add.js.
   Senza JavaScript tutto funziona come link e form normali. */

(() => {
  'use strict';

  const PARTI = ['colori', 'barra', 'chip', 'risultati', 'gruppi', 'mostra'];   // 'colori': in collezione e ricerca non c'è più (21/10/2026, il Colore sta nei gruppi del foglio); sulla pagina grafica è la riga dei chip dei tagli (main-grafica)

  class MesCollezione extends HTMLElement {
    connectedCallback() {
      this.sezione = this.dataset.sezione;
      this.controller = null;
      this.addEventListener('click', (e) => this._suClick(e));
      this._avviaColonne();
      this.addEventListener('change', (e) => this._suChange(e));
      // ultimo modo di interagire (21/10/2026): dopo un tocco il fuoco non si rimette da codice (su iPhone un focus() su un campo del
      // modulo, con un altro campo già a fuoco, riapre la scelta o la tastiera); con mouse e tastiera sì, come prima
      this.addEventListener('pointerdown', (e) => { this._daTocco = e.pointerType === 'touch' || e.pointerType === 'pen'; }, { capture: true, passive: true });
      this.addEventListener('keydown', () => { this._daTocco = false; }, { capture: true, passive: true });
      this.addEventListener('submit', (e) => { if (e.target.matches('[data-filtri-form]')) { e.preventDefault(); this._applicaForm(); } });
      /* i drawer mettono una voce nella history (tasto indietro del telefono = chiudi) senza cambiare l'URL:
         la griglia si ricarica solo se l'indirizzo è davvero cambiato (indietro/avanti tra filtri) */
      this.urlCorrente = location.href;
      window.addEventListener('popstate', () => {
        // Indietro con il foglio dei filtri aperto e filtri scelti dentro (D1, 20/10/2026): il foglio si chiude (lo fa mes-drawer) e i filtri
        // RESTANO: la pagina filtrata torna in cima alla cronologia come voce propria, il prossimo Indietro va alla collezione senza filtri
        if (this.filtriNelFoglio && !(history.state && history.state.drawer) && location.href !== this.filtriNelFoglio) {
          history.pushState({}, '', this.filtriNelFoglio);
          this.filtriNelFoglio = null; this.urlCorrente = location.href;
          return;
        }
        if (location.href !== this.urlCorrente) this.carica(location.href, { pushState: false });
      });
      // foglio chiuso in un altro modo (X, trascinamento, "Mostra N prodotti", pageshow): la sua voce è già la pagina filtrata (mes-drawer, chiudi)
      this.addEventListener('mes:drawer-chiuso', () => { this.filtriNelFoglio = null; });
      this._ultimiPezzi();
      this._pagineIndietro();
    }

    /* dopo "Mostra altri" l'indirizzo porta ?page=N; se la pagina torna con Indietro RICARICATA (Safari l'aveva tolta dalla memoria) Shopify dà
       solo la pagina N: si rimettono davanti le pagine da 1 a N-1, nell'ordine, poi la rete del ritorno (theme.liquid) riprova l'altezza salvata
       (D3, 20/10/2026). Un indirizzo con ?page=N aperto da un link resta la sola pagina N, condivisibile com'è */
    async _pagineIndietro() {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      const url = new URL(location.href);
      const n = parseInt(url.searchParams.get('page'), 10);
      const griglia = this.querySelector('[data-griglia]');
      if (!nav || nav.type !== 'back_forward' || !(n > 1) || !griglia) return;
      try {
        const docs = await Promise.all(Array.from({ length: n - 1 }, (_, i) => {
          const u = new URL(url.href); u.searchParams.set('page', i + 1); u.searchParams.set('section_id', this.sezione);
          return MES.testo(u.href).then((html) => new DOMParser().parseFromString(html, 'text/html'));
        }));
        const prima = griglia.firstElementChild;
        docs.forEach((doc) => doc.querySelectorAll('[data-griglia] > li').forEach((li) => griglia.insertBefore(li, prima)));
        this._sizesFoto();
        if (window.mesRitorno) window.mesRitorno.riprova('pagine 1-' + (n - 1) + ' rimesse davanti');
      } catch (e) { /* resta la sola pagina N, com'era prima */ }
    }

    /* 1 o 2 colonne sul telefono (20/10/2026): data-colonne su questo elemento (1 di default, lo script inline nella sezione mette 2
       dal localStorage prima del primo disegno); i pulsanti della barra lo cambiano e la scelta resta in localStorage "mes-colonne".
       Cambiare colonne non fa comparire niente da display: none: cambia solo la griglia e `sizes` delle foto (regola dei tocchi) */
    _avviaColonne() {
      this._conColonne = !!this.querySelector('[data-colonne-gruppo]');   // la pagina grafica non ha il selettore e resta com'è
      if (!this._conColonne) return;
      let scelta = '1';
      try { if (localStorage.getItem('mes-colonne') === '2') scelta = '2'; } catch (e) {}
      this._applicaColonne(scelta, false);
    }
    _applicaColonne(scelta, salva = true) {
      if (!this._conColonne || (scelta !== '1' && scelta !== '2')) return;
      this.dataset.colonne = scelta;
      if (salva) try { localStorage.setItem('mes-colonne', scelta); } catch (e) {}
      this.querySelectorAll('[data-colonne-gruppo] [data-colonne]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.colonne === scelta)));
      this._sizesFoto();
    }
    /* `sizes` delle foto della griglia (card e Ultimi pezzi) secondo le colonne: a 1 colonna la card è larga 100vw − 32 px, a 2 metà
       schermo meno i margini; il browser sceglie da solo la larghezza giusta del srcset (niente foto sgranate, niente file inutili) */
    _sizesFoto() {
      if (!this._conColonne) return;
      const due = this.dataset.colonne === '2';
      const sizes = due ? '(min-width: 990px) 25vw, calc(50vw - 24px)' : '(min-width: 990px) 25vw, (min-width: 750px) calc(50vw - 24px), calc(100vw - 32px)';
      this.querySelectorAll('.card:not(.card--riga) .card__media img[sizes]').forEach((img) => { if (img.sizes !== sizes) img.sizes = sizes; });
    }

    /* link di filtro (riga colore, chip, cancella tutto) e "Mostra altri" */
    _suClick(e) {
      const col = e.target.closest('[data-colonne-gruppo] [data-colonne]');
      if (col) {
        const aveva = document.activeElement === col;
        this._applicaColonne(col.dataset.colonne);
        // sul telefono il pulsante premuto sparisce (si vede quello dell'altra disposizione): il fuoco passa all'altro, solo se l'aveva lui
        if (aveva && !col.getClientRects().length) this.querySelector(`[data-colonne-gruppo] [data-colonne]:not([data-colonne="${col.dataset.colonne}"])`)?.focus({ preventScroll: true });
        return;
      }
      const link = e.target.closest('[data-filtro-link]');
      if (link) {
        if (link.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        e.preventDefault();
        this.carica(link.href);
        return;
      }
      const altri = e.target.closest('[data-mostra-altri]');
      if (altri) { e.preventDefault(); this.mostraAltri(altri); }
    }

    /* checkbox e prezzo nel drawer, select ordinamento */
    _suChange(e) {
      if (e.target.matches('[data-ordina]')) {
        // il nome scritto nel pulsante cambia subito, prima che arrivi la sezione nuova
        const testo = e.target.closest('.collezione__ordina')?.querySelector('[data-ordina-testo]');
        const scelta = e.target.selectedOptions[0];
        if (testo && scelta) testo.textContent = scelta.dataset.breve || scelta.text;
        // scelta fatta con il dito: la select si lascia (su iPhone chiude la scelta nativa, che restava agganciata al campo a fuoco)
        if (this._daTocco) e.target.blur();
        const url = new URL(location.href);
        url.searchParams.set('sort_by', e.target.value);
        url.searchParams.delete('page');
        this.carica(url.href);
        return;
      }
      if (e.target.matches('[data-filtro-input]')) {
        if (e.target.type === 'number') { clearTimeout(this._t); this._t = setTimeout(() => this._applicaForm(), 500); }
        else this._applicaForm();
      }
    }

    _applicaForm() {
      const form = this.querySelector('[data-filtri-form]');
      if (!form) return;
      const dati = new FormData(form);
      const url = new URL(form.action, location.origin);
      // conservo i filtri che non stanno nel form (il Colore vive nella riga sopra la griglia)
      const attuale = new URL(location.href);
      attuale.searchParams.forEach((v, k) => { if (k.startsWith('filter.') && !form.querySelector(`[name="${k}"]`)) url.searchParams.append(k, v); });
      dati.forEach((v, k) => { if (v !== '') url.searchParams.append(k, v); });
      // ordinamento attuale (21/10/2026): il campo nascosto sort_by del form è quello scritto al caricamento della pagina e non si
      // aggiorna quando l'ordinamento cambia via AJAX; senza questa riga un filtro messo dopo un cambio di ordinamento lo riportava indietro
      const ordina = this.querySelector('[data-ordina]');
      if (ordina && ordina.value) url.searchParams.set('sort_by', ordina.value);
      this.carica(url.href);
    }

    async carica(href, { pushState = true } = {}) {
      const url = new URL(href, location.origin);
      url.searchParams.delete('page');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const risultati = this.querySelector('[data-parte="risultati"]');
      risultati?.setAttribute('aria-busy', 'true');
      try {
        const fetchUrl = new URL(url.href);
        fetchUrl.searchParams.set('section_id', this.sezione);
        const html = await MES.testo(fetchUrl.href, { signal: this.controller.signal });
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this._sostituisci(doc);
        if (pushState) {
          // con il foglio dei filtri aperto la voce di cronologia è la sua e prende l'indirizzo filtrato; Indietro chiude il foglio e
          // tiene i filtri (popstate qui sopra, D1 del 20/10/2026)
          if (history.state && history.state.drawer) { history.replaceState(history.state, '', url.href); this.filtriNelFoglio = url.href; }
          else history.pushState({}, '', url.href);
        }
        this.urlCorrente = location.href;
        this._ultimiPezzi();
      } catch (err) {
        if (err.name !== 'AbortError') location.href = url.href; // ripiego: navigazione normale
      } finally {
        risultati?.setAttribute('aria-busy', 'false');
      }
    }

    _sostituisci(doc) {
      // stato di apertura dei gruppi del drawer, da conservare
      const aperti = new Set(Array.from(this.querySelectorAll('[data-gruppo]')).filter((d) => d.open).map((d) => d.dataset.gruppo));
      const attivo = document.activeElement;
      const nomeAttivo = attivo && this.contains(attivo) ? attivo.getAttribute('name') + '|' + attivo.getAttribute('value') : null;
      const eraOrdina = attivo && this.contains(attivo) && attivo.matches('[data-ordina]');
      // il cliente sta scrivendo un prezzo (Da/A): i gruppi non si sostituiscono, altrimenti il numero sparisce e la tastiera si chiude
      // (revisione del 20/10/2026); i conteggi del drawer si aggiornano al prossimo giro
      const scrivePrezzo = attivo && attivo.matches && attivo.matches('[data-parte="gruppi"] input[type="number"]');
      PARTI.forEach((p) => {
        if (p === 'gruppi' && scrivePrezzo) return;
        const nuovo = doc.querySelector(`[data-parte="${p}"]`);
        const vecchio = this.querySelector(`[data-parte="${p}"]`);
        if (!nuovo || !vecchio) return;
        if (p === 'barra') this._sostituisciBarra(vecchio, nuovo);
        else vecchio.replaceWith(nuovo);
      });
      this.querySelectorAll('[data-gruppo]').forEach((d) => { d.open = aperti.has(d.dataset.gruppo); });
      // l'ordinamento non si sostituisce più (_sostituisciBarra): se aveva il fuoco lo tiene da solo, nessun focus() da rimettere.
      // Il fuoco sulle caselle dei filtri si rimette solo se il cambio non arriva da un tocco
      if (!eraOrdina && nomeAttivo && !this._daTocco) {
        const [n, v] = nomeAttivo.split('|');
        this.querySelector(`[name="${n}"][value="${v}"]`)?.focus({ preventScroll: true });
      }
      // i pulsanti "apri drawer" nella barra sono nuovi: mes-drawer li ricollega
      this.querySelectorAll('mes-drawer').forEach((d) => d.connectedCallback());
      this._applicaColonne(this.dataset.colonne || '1', false);   // barra e foto nuove: aria-pressed e sizes
    }

    /* barra ridisegnata senza toccare l'ordinamento (21/10/2026, bug solo iOS): prima la barra si sostituiva intera, select compresa, e il
       codice rimetteva il fuoco sulla select nuova; su iPhone quel focus() riapriva da solo l'elenco delle scelte appena chiuso. Ora la
       <label> dell'ordinamento resta lo stesso elemento (si aggiornano la voce scelta e il testo del pulsante) e si sostituiscono solo
       i pezzi intorno: "Filtri" con il numero dei filtri attivi e i pulsanti 1/2 colonne */
    _sostituisciBarra(vecchio, nuovo) {
      const vo = vecchio.querySelector('.collezione__ordina'), no = nuovo.querySelector('.collezione__ordina');
      if (!vo || !no || !vo.parentElement || !no.parentElement) { vecchio.replaceWith(nuovo); return; }
      const sel = vo.querySelector('[data-ordina]'), nuovaSel = no.querySelector('[data-ordina]');
      if (sel && nuovaSel && sel.value !== nuovaSel.value) sel.value = nuovaSel.value;
      const t = vo.querySelector('[data-ordina-testo]'), tn = no.querySelector('[data-ordina-testo]');
      if (t && tn && t.textContent !== tn.textContent) t.textContent = tn.textContent;
      const prima = [], dopo = []; let oltre = false;
      Array.from(no.parentElement.childNodes).forEach((n) => { if (n === no) { oltre = true; return; } (oltre ? dopo : prima).push(n); });
      Array.from(vo.parentElement.childNodes).forEach((n) => { if (n !== vo) n.remove(); });
      vo.before(...prima); vo.after(...dopo);
    }

    async mostraAltri(bottone) {
      if (bottone.dataset.inCorso) return;   // è un link: disabled non fa niente, e un doppio tocco su rete lenta accodava le stesse card due volte (revisione del 20/10/2026)
      bottone.dataset.inCorso = '1';
      const url = new URL(bottone.href, location.origin);
      url.searchParams.set('section_id', this.sezione);
      bottone.setAttribute('aria-busy', 'true');
      bottone.disabled = true;
      try {
        const html = await MES.testo(url.href);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nuoveCard = doc.querySelectorAll('[data-griglia] > li');
        const griglia = this.querySelector('[data-griglia]');
        const primaNuova = nuoveCard[0];
        nuoveCard.forEach((li) => griglia.appendChild(li));
        const nuovaPag = doc.querySelector('[data-paginazione]');
        const vecchiaPag = this.querySelector('[data-paginazione]');
        if (nuovaPag && vecchiaPag) vecchiaPag.replaceWith(nuovaPag);
        this._sizesFoto();
        primaNuova?.querySelector('a, button')?.focus({ preventScroll: true });
        // l'indirizzo resta condivisibile con la pagina raggiunta
        const pagina = new URL(bottone.href, location.origin).searchParams.get('page');
        const attuale = new URL(location.href);
        if (pagina) attuale.searchParams.set('page', pagina);
        history.replaceState({}, '', attuale.href);
        this.urlCorrente = location.href;
      } catch (err) {
        location.href = bottone.href;
      }
    }

    /* Ultimi pezzi: gli archiviati che rientrano in questa collezione e nei filtri attivi */
    async _ultimiPezzi() {
      const blocco = this.querySelector('[data-ultimi-pezzi]');
      if (!blocco) return;
      const url = new URL(blocco.dataset.url, location.origin);
      new URL(location.href).searchParams.forEach((v, k) => { if (k.startsWith('filter.') || k === 'sort_by') url.searchParams.append(k, v); });
      if (blocco.dataset.criterio) new URLSearchParams(blocco.dataset.criterio).forEach((v, k) => url.searchParams.append(k, v));
      url.searchParams.set('section_id', 'ultimi-pezzi');
      try {
        // con due filtri rapidi vince l'ultimo: la richiesta viaggia con il segnale dell'ultimo carica() (revisione del 20/10/2026)
        const html = await MES.testo(url.href, this.controller ? { signal: this.controller.signal } : undefined);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const interno = doc.querySelector('.ultimi__interno');
        blocco.querySelector('.ultimi__interno')?.remove();
        if (interno) { blocco.appendChild(interno); blocco.hidden = false; this._sizesFoto(); }
        else blocco.hidden = true;
      } catch (err) { if (!(err && err.name === 'AbortError')) blocco.hidden = true; }
    }
  }
  if (!customElements.get('mes-collezione')) customElements.define('mes-collezione', MesCollezione);   // lo script può essere incluso da più sezioni nella stessa pagina (home)

  /* ---------------------------------------------------------------- aggiunta rapida */
  class MesAggiuntaRapida extends HTMLElement {
    connectedCallback() {
      this.esito = this.querySelector('.card__esito');
      this.nelMenu = !!this.closest('#menu-drawer');   // dentro il menu (29/09/2026): il carrello non si apre, la conferma sta sulla card
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-variante]');
        if (b && !b.disabled) this.aggiungi(b);
      });
    }

    async aggiungi(bottone) {
      if (this._inCorso) return;               // doppio tocco = un'aggiunta sola (voce 18)
      this._inCorso = true;   // letto anche dal "+" del menu: durante la richiesta il pannello non si richiude
      bottone.disabled = true;
      bottone.setAttribute('aria-busy', 'true');
      bottone.classList.add('card__taglia--in-corso');
      try {
        const drawer = document.querySelector('mes-carrello');
        const corpo = { items: [{ id: Number(bottone.dataset.variante), quantity: 1, properties: MES.proprieta(this.dataset.proprieta) }] };
        if (drawer) corpo.sections = 'carrello-drawer';   // una richiesta sola: torna anche il drawer renderizzato
        const r = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(corpo)
        });
        if (!r.ok) { const e = new Error('risposta'); e.messaggio = await MES.messaggioRisposta(r); throw e; }
        const dati = await r.json();
        if (drawer) {
          document.dispatchEvent(new CustomEvent('mes:carrello-aggiornato', { detail: { html: dati.sections && dati.sections['carrello-drawer'], origine: this.nelMenu ? 'menu' : 'card' } }));
          if (this.nelMenu) this._mostra(document.documentElement.dataset.msgAggiunto, true);
        } else {
          const carrello = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
          this._aggiornaContatore(carrello.item_count);
          this._mostra(document.documentElement.dataset.msgAggiunto, true);
        }
      } catch (err) {
        // il messaggio preciso del server ("già esaurito", "quantità massima") vale più del generico "Riprova"
        this._mostra(err.messaggio || document.documentElement.dataset.msgErroreAggiunta, false);
      } finally {
        bottone.disabled = false;
        bottone.removeAttribute('aria-busy');
        bottone.classList.remove('card__taglia--in-corso');
        this._inCorso = false;
        // dal menu il carrello non si apre e nessuno riprende il focus perso con disabled: torna sulla taglia
        if (this.nelMenu && document.activeElement === document.body) bottone.focus({ preventScroll: true });
      }
    }

    _aggiornaContatore(n) {
      document.querySelectorAll('[data-carrello-contatore]').forEach((el) => { el.textContent = n; el.classList.toggle('header__contatore--vuoto', n === 0); });
    }

    _mostra(testo, ok) {
      if (!this.esito) return;
      // nel menu il pannello delle taglie può essere richiuso dal "+": la conferma o l'errore si vedono sempre
      if (this.nelMenu && this.hidden) { const piu = this.closest('.card')?.querySelector('[data-piu]'); if (piu) MES.apriTaglie(piu, true); }
      this.esito.textContent = '';
      this.esito.hidden = false;
      this.esito.classList.toggle('card__esito--errore', !ok);
      this.esito.append(testo || '');
      if (ok) {
        const a = document.createElement('a');
        a.href = window.Shopify.routes.root + 'cart';
        a.textContent = document.documentElement.dataset.msgVaiAlCarrello || '';
        // se c'è il drawer, il link lo apre (dal menu chiude il menu: un pannello alla volta); senza JS resta /cart
        a.addEventListener('click', (e) => { const c = document.querySelector('mes-carrello'); if (c && typeof c.apri === 'function') { e.preventDefault(); c.apri(a); } });
        this.esito.append(' ', a);
      }
      clearTimeout(this._t);
      this._t = setTimeout(() => { this.esito.hidden = true; }, 6000);
    }
  }
  if (!customElements.get('mes-aggiunta-rapida')) customElements.define('mes-aggiunta-rapida', MesAggiuntaRapida);   // lo script può essere incluso più volte (home) o caricato dal menu
})();
